import { filter, interval, skip, takeUntil, Subscription } from 'rxjs';

import { sessionTimeoutWarning, userInteraction } from '../system-utils/events-manager';
import { UserInteractions } from '../system-utils/system-types';
import Config from '../system-utils/config-manager';

let instance: SessionManager = null;

/**
 * Singleton class to manage session timeouts
 *
 * Subscribe to the sessionTimeoutWarning event to be notified how many seconds until the session times out.
 */
export class SessionManager {
  private _defaultTimeoutMinutes: number;
  private _timeoutTime: number;
  private _everySecondUntilTimeoutSub: Subscription;
  private _userInteractionSub: Subscription;
  private _timerResetOnUserInteractionEnabled: boolean;

  constructor() {
    if (!instance) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      instance = this;
    }

    this._defaultTimeoutMinutes = Config.get('default_session_timeout_minutes', 5);
    this._timeoutTime = -1;
    this._everySecondUntilTimeoutSub = null;
    this._userInteractionSub = null;
    this._timerResetOnUserInteractionEnabled = false;

    return instance;
  }

  private _updateTimeout() {
    const currentDate = new Date();
    currentDate.setMilliseconds(0);
    let timeoutInSeconds = (this._timeoutTime - currentDate.getTime()) / 1000;
    timeoutInSeconds = Math.max(timeoutInSeconds, 0);
    if (sessionTimeoutWarning.value.timeoutIn !== timeoutInSeconds) {
      sessionTimeoutWarning.next({ timeoutIn: timeoutInSeconds });
    }
  }

  /**
   * Starts the timer or updates the end time if the timer has already started.
   * @param timeoutMinutes an optional timeout in minutes to override the default 5 minute timeout.
   */
  startTimer(timeoutMinutes?: number) {
    this._timerResetOnUserInteractionEnabled = true;
    const timeoutInMs = (timeoutMinutes !== undefined ? timeoutMinutes : this._defaultTimeoutMinutes) * 60 * 1000;
    const currentDate = new Date();
    const timeoutDate = new Date(currentDate.getTime() + timeoutInMs);
    timeoutDate.setMilliseconds(0);
    this._timeoutTime = timeoutDate.getTime();

    this._updateTimeout();

    if (!this._everySecondUntilTimeoutSub) {
      const everySecondUntilTimeout = interval(1000).pipe(
        takeUntil(
          sessionTimeoutWarning.pipe(
            filter(({ timeoutIn }) => {
              const done = timeoutIn < 1;
              if (done) this._cancelTimer();
              return done;
            }),
          ),
        ),
      );

      this._everySecondUntilTimeoutSub = everySecondUntilTimeout.subscribe(() => {
        this._updateTimeout();
      });
    }

    if (!this._userInteractionSub) {
      // Skip 1 since we only want events which happen since subscribing
      this._userInteractionSub = userInteraction.pipe(skip(1)).subscribe(({ type }) => {
        if (this._timerResetOnUserInteractionEnabled && (type === UserInteractions.CLICK || type === UserInteractions.WHEEL)) {
          this.resetTimer();
        }
      });
    }
  }

  /**
   * Cancels the timer and unsubscribes from all subscriptions
   */
  _cancelTimer() {
    this._timeoutTime = -1;
    if (this._everySecondUntilTimeoutSub) this._everySecondUntilTimeoutSub.unsubscribe();
    this._everySecondUntilTimeoutSub = null;
    if (this._userInteractionSub) this._userInteractionSub.unsubscribe();
    this._userInteractionSub = null;
  }

  /**
   * Resets the timer by calling startTimer
   */
  resetTimer() {
    this.startTimer();
  }

  /**
   * Disables the timer from being reset on a user interaction event.
   * Used to stop the timer resetting when the session expiring soon popup appears.
   */
  disableOnUserInteractionTimerReset() {
    this._timerResetOnUserInteractionEnabled = false;
  }

  /**
   * Clears the session and cancels the timer
   */
  clearSession() {
    // TODO: add to tests
    this._cancelTimer();
    sessionTimeoutWarning.next({ timeoutIn: 0 });
  }
}

/**
 * Creates the only instance of the SessionManager.
 * @returns the SessionManager
 */
export const getSessionManager = () => {
  return new SessionManager();
};
